/**
 * リアルタイムチャット WebSocket接続カスタムフック（利用者側）
 *
 * AppSync Events WebSocketによるリアルタイムメッセージング。
 * 管理者版（tenant-elive）をベースに、利用者向けに以下を変更:
 * - メッセージ送信を student タイプ固定
 * - 利用者側 ChatMessage 型にマッピング
 * - 履歴取得は admin=false（hidden メッセージはサーバー側で除外）
 * - モデレーションイベント受信時は isHidden フラグを更新
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { ChatMessage } from '../types/chat';
import type {
  AppSyncEventsConfig,
  SendMessageData,
  MessageEventPayload,
} from '../api/realtimeChat';
import { getChatConfig, getMessageHistory, publishMessage } from '../api/realtimeChat';
import { ensureFrontendSettings } from '../api/frontendSettings';
import { MAX_BACKOFF_EXPONENT, backoffDelayMs, DEFAULT_KA_TIMEOUT_MS } from '../utils/wsBackoff';

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface UseRealtimeChatOptions {
  tenantId: string;
  lectureId: string;
  /** 現在のユーザーID（投稿者本人判定に使用） */
  currentUserId?: string;
  onMessage?: (message: ChatMessage) => void;
  onModeration?: (action: 'hide' | 'unhide', messageId: string) => void;
  onError?: (error: Error) => void;
  /** false にすると WebSocket 接続・履歴取得の両方を行わない */
  enabled?: boolean;
  /**
   * false にすると WebSocket 接続のみ行わない（履歴取得は enabled に従う）。
   * アーカイブ視聴では過去ログの閲覧（履歴取得）のみ行い、
   * 接続（ログイン数記録に影響）は行わないために使用する。
   */
  connectEnabled?: boolean;
}

export interface UseRealtimeChatReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  sendMessage: (data: SendMessageData) => Promise<void>;
  connect: () => void;
  disconnect: () => void;
  clearMessages: () => void;
  reloadHistory: () => Promise<void>;
}

export function useRealtimeChat(options: UseRealtimeChatOptions): UseRealtimeChatReturn {
  const {
    tenantId,
    lectureId,
    currentUserId,
    onMessage,
    onModeration,
    onError,
    enabled = true,
    connectEnabled = true,
  } = options;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const configRef = useRef<AppSyncEventsConfig | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  // keep-alive（ka）途絶検知用（STU-H3対応）
  const kaWatchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const kaTimeoutMsRef = useRef(DEFAULT_KA_TIMEOUT_MS);
  // 初回購読が完了したか（2回目以降の subscribe_success ＝再接続と判定するため）
  const hasSubscribedOnceRef = useRef(false);
  // 再同期の多重実行ガード
  const resyncInFlightRef = useRef(false);
  // onclose の自動再接続判定用（クロージャーの stale 値を避けるため ref で保持）
  const shouldReconnectRef = useRef(enabled && connectEnabled);
  shouldReconnectRef.current = enabled && connectEnabled;

  // ------------------------------------------------------------------
  // 履歴取得（全件ページネーション）
  // AbortSignal を受け取り、Strict Mode の二重マウントでも安全にキャンセル可能。
  // 戻り値: 取得に成功したら true、失敗したら false（呼び出し側のリトライ判定用。STU-8対応）
  // ------------------------------------------------------------------
  const loadHistory = useCallback(async (signal?: AbortSignal): Promise<boolean> => {
    if (!tenantId || !lectureId) return true;

    setIsLoading(true);
    setError(null);

    try {
      let allMessages: ChatMessage[] = [];
      let currentNextToken: string | null = null;

      do {
        if (signal?.aborted) return true;
        const result = await getMessageHistory(tenantId, lectureId, 100, currentNextToken);
        if (signal?.aborted) return true;
        allMessages = [...allMessages, ...result.messages];
        currentNextToken = result.nextToken;
        if (!result.hasMore || !result.nextToken) break;
      } while (currentNextToken);

      if (signal?.aborted) return true;

      // タイムスタンプ昇順（古い→新しい）に明示的ソート。
      // API のソート順に依存せず、常に最新メッセージが末尾に来るようにする
      allMessages.sort((a, b) => {
        const ta = new Date(a.timestamp).getTime();
        const tb = new Date(b.timestamp).getTime();
        return ta - tb;
      });
      setMessages(allMessages);
      return true;
    } catch (err) {
      if (signal?.aborted) return true;
      const errorMessage = err instanceof Error ? err.message : 'メッセージ履歴の読み込みに失敗しました';
      console.error('[Chat] メッセージ履歴読み込みエラー:', err);
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
      return false;
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, [tenantId, lectureId, onError]);

  // ------------------------------------------------------------------
  // 再接続時の履歴再同期（受講生B-4対応）
  // 切断中に投稿されたメッセージ・モデレーション（hide/delete_all）は WS では
  // 届かないため、再購読成功時に履歴 API から再取得して現在のメッセージとマージする。
  // ------------------------------------------------------------------
  const resyncHistory = useCallback(async () => {
    if (!tenantId || !lectureId) return;
    if (resyncInFlightRef.current) return;
    resyncInFlightRef.current = true;
    const fetchStartedAt = Date.now();

    try {
      let historyMessages: ChatMessage[] = [];
      let currentNextToken: string | null = null;

      do {
        const result = await getMessageHistory(tenantId, lectureId, 100, currentNextToken);
        historyMessages = [...historyMessages, ...result.messages];
        currentNextToken = result.nextToken;
        if (!result.hasMore || !result.nextToken) break;
      } while (currentNextToken);

      if (!isMountedRef.current) return;

      setMessages((prev) => {
        const keyOf = (m: ChatMessage) => m.messageId ?? m.id;
        const historyKeys = new Set(historyMessages.map(keyOf));
        // 履歴に含まれない既存メッセージのうち、以下のみ残す:
        // - 自分のメッセージ（非表示化されても本人には表示し続ける仕様のため、
        //   hidden 除外済みの受講生向け履歴には含まれない）
        // - 履歴取得開始以降に WS で届いた新着（履歴スナップショットに未反映の可能性がある）
        // それ以外（履歴から消えた他人のメッセージ）は切断中に非表示/削除されたとみなし除外する
        const extras = prev.filter((m) => {
          if (historyKeys.has(keyOf(m))) return false;
          const isOwn = !!currentUserId && m.userId === currentUserId;
          const isFresh = new Date(m.timestamp).getTime() >= fetchStartedAt;
          return isOwn || isFresh;
        });
        const merged = [...historyMessages, ...extras];
        merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        console.log('[Chat] 再接続時の履歴再同期完了:', { history: historyMessages.length, extras: extras.length });
        return merged;
      });
    } catch (err) {
      // 再同期失敗時は現状のメッセージ表示を維持する（次回の再接続で再試行される）
      console.warn('[Chat] 再接続時の履歴再同期に失敗:', err);
    } finally {
      resyncInFlightRef.current = false;
    }
  }, [tenantId, lectureId, currentUserId]);

  // connect の useCallback を再生成せずに最新の再同期処理を呼べるように ref 経由で保持する
  const resyncHistoryRef = useRef(resyncHistory);
  resyncHistoryRef.current = resyncHistory;

  // ------------------------------------------------------------------
  // WebSocket 接続
  // ------------------------------------------------------------------
  const connect = useCallback(async () => {
    if (!isMountedRef.current) return;

    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    // 設定を取得（frontend-settings API。取得失敗時はエラー表示）
    try {
      await ensureFrontendSettings();
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : '設定の取得に失敗しました';
      setError(errorMsg);
      onError?.(new Error(errorMsg));
      // WebSocket生成前の失敗（オフライン中の設定取得失敗など）はoncloseが発火しないため、
      // ここで再試行をスケジュールしないと再接続ループが停止する
      if (isMountedRef.current && shouldReconnectRef.current) {
        reconnectAttemptsRef.current = Math.min(reconnectAttemptsRef.current + 1, MAX_BACKOFF_EXPONENT);
        const delay = backoffDelayMs(reconnectAttemptsRef.current);
        console.log(`[Chat] ${delay}ms後に接続を再試行します (試行 ${reconnectAttemptsRef.current})`);
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
      return;
    }

    // 取得待ちの間に接続・アンマウントが進んでいたら中断
    if (!isMountedRef.current ||
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const config = getChatConfig();
    configRef.current = config;

    if (!config.realtimeEndpoint || !config.apiKey) {
      const errorMsg = 'チャット設定が不完全です';
      setError(errorMsg);
      onError?.(new Error(errorMsg));
      return;
    }

    // Lambda Authorizer 認証（SEC-L4対応で AppSync組み込みAPIキー（x-api-key）から
    // Authorization トークンに変更）
    const authorization = {
      Authorization: config.apiKey,
      host: config.httpEndpoint,
    };

    const authHeader = btoa(JSON.stringify(authorization))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    // 破棄しきれていない古いソケット（CLOSING中など）が残っていれば完全に破棄する。
    // 古いソケットのoncloseが新しい接続のwsRefをnull化して再接続を誘発すると、
    // 購読済みソケットが参照を失ったまま残り（ゾンビ接続）メッセージが多重受信される
    if (wsRef.current) {
      const staleWs = wsRef.current;
      staleWs.onopen = null;
      staleWs.onmessage = null;
      staleWs.onerror = null;
      staleWs.onclose = null;
      if (staleWs.readyState === WebSocket.CONNECTING || staleWs.readyState === WebSocket.OPEN) {
        staleWs.close();
      }
      wsRef.current = null;
    }

    const ws = new WebSocket(config.realtimeEndpoint, [
      'aws-appsync-event-ws',
      `header-${authHeader}`,
    ]);
    wsRef.current = ws;

    // ------------------------------------------------------------------
    // keep-alive（ka）途絶の検知（STU-H3対応）
    // モバイル回線等では TCP 接続が無音のまま死に、onclose も offline イベントも
    // 発火しない「ゾンビ接続」が発生する。AppSync は接続維持中 ka を定期送信するため、
    // connectionTimeoutMs の間なにも受信しなければ接続が死んだと判断し、
    // 死んだソケットを即座に破棄して再接続する（close 完了の onclose は待たない）
    // ------------------------------------------------------------------
    const clearKaWatchdog = () => {
      if (kaWatchdogTimerRef.current) {
        clearTimeout(kaWatchdogTimerRef.current);
        kaWatchdogTimerRef.current = null;
      }
    };
    const resetKaWatchdog = () => {
      clearKaWatchdog();
      kaWatchdogTimerRef.current = setTimeout(() => {
        if (wsRef.current !== ws) return;
        console.warn('[Chat] keep-alive途絶を検知（ゾンビ接続）、再接続します');
        setIsConnected(false);
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        wsRef.current = null;
        try {
          ws.close(4001, 'Keep-alive timeout');
        } catch {
          // すでに閉じている場合は無視
        }
        if (isMountedRef.current && shouldReconnectRef.current) {
          connect();
        }
      }, kaTimeoutMsRef.current);
    };

    ws.onopen = () => {
      // 既に新しいソケットに置き換えられている場合は自分を閉じて終了（ゾンビ接続防止）
      if (wsRef.current !== ws) {
        ws.close();
        return;
      }
      if (!isMountedRef.current) {
        ws.close();
        return;
      }
      ws.send(JSON.stringify({ type: 'connection_init' }));
    };

    ws.onmessage = (event) => {
      // 置き換え済みの古いソケットからの受信は無視（メッセージ多重受信防止）
      if (wsRef.current !== ws) return;
      // 受信（ka含む）があった＝接続は生きているため、途絶検知タイマーをリセット
      resetKaWatchdog();
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'connection_ack') {
          // AppSync が通知するアイドルタイムアウトを ka 途絶検知のしきい値に使う
          if (typeof data.connectionTimeoutMs === 'number' && data.connectionTimeoutMs > 0) {
            kaTimeoutMsRef.current = data.connectionTimeoutMs;
            resetKaWatchdog();
          }
          setIsConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;

          const channelPath = `chat/${tenantId}/${lectureId}/messages`;
          const cfg = configRef.current;
          if (!cfg) return;

          ws.send(
            JSON.stringify({
              type: 'subscribe',
              id: generateUUID(),
              channel: channelPath,
              authorization: {
                Authorization: cfg.apiKey,
                host: cfg.httpEndpoint,
              },
            }),
          );
          return;
        }

        // keep-alive は無視
        if (data.type === 'ka') return;

        if (data.type === 'connection_error') {
          console.error('[Chat] 接続エラー:', data.errors);
          return;
        }

        if (data.type === 'subscribe_success') {
          // 2回目以降の購読成功＝再接続。切断中に取りこぼしたメッセージ・
          // モデレーションを履歴 API から再同期する（受講生B-4対応）
          if (hasSubscribedOnceRef.current) {
            console.log('[Chat] 再購読成功 → チャット履歴を再同期');
            resyncHistoryRef.current();
          }
          hasSubscribedOnceRef.current = true;
          return;
        }

        if (data.type === 'subscribe_error') {
          console.error('[Chat] サブスクライブエラー:', data);
          return;
        }

        // データイベント処理
        if (data.type === 'data' && data.event) {
          let payload: MessageEventPayload;

          if (typeof data.event === 'string') {
            const firstParse = JSON.parse(data.event);
            payload = typeof firstParse === 'string' ? JSON.parse(firstParse) : firstParse;
          } else {
            payload = data.event;
          }

          // 新着メッセージ
          if (payload.type === 'message') {
            const newMessage: ChatMessage = {
              // messageId があればそれを使用し、ない場合は timestamp + ランダム値にする。
              // timestamp のみだと同一ミリ秒の複数メッセージで id（React の key・
              // 再同期時の重複判定キー）が衝突する（STU-L5対応）
              id: payload.messageId != null
                ? String(payload.messageId)
                : `${payload.timestamp || Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
              userId: payload.userId || '',
              userName: payload.userName || '',
              userType: payload.userType === 'admin' ? 'instructor' : 'student',
              message: payload.message || '',
              timestamp: new Date(payload.timestamp || Date.now()).toISOString(),
              isHidden: payload.status === 'hidden',
              ...(payload.messageId != null && { messageId: payload.messageId }),
              ...(payload.status != null && { status: payload.status }),
              ...(payload.hasNgWords != null && { hasNgWords: payload.hasNgWords }),
              ...(payload.isNgUser != null && { isNgUser: payload.isNgUser }),
            };

            console.log('[useRealtimeChat] 新着メッセージ受信:', {
              messageId: newMessage.id,
              userId: newMessage.userId,
              hasNgWords: newMessage.hasNgWords,
              isNgUser: newMessage.isNgUser,
              payloadHasNgWords: payload.hasNgWords,
              payloadIsNgUser: payload.isNgUser,
            });

            setMessages((prev) => [...prev, newMessage]);
            onMessage?.(newMessage);
          }

          // モデレーションイベント（管理者が hide/unhide した場合の反映）
          if (payload.type === 'moderation' && payload.action) {
            const action = payload.action;
            
            // 全削除イベント
            if (action === 'delete_all') {
              console.log('[Chat] 管理者が全メッセージを削除しました');
              setMessages([]);
              onModeration?.('delete_all' as any, '');
              return;
            }
            
            // 個別メッセージの非表示/表示復元
            const targetMessageId = payload.messageId;
            if (!targetMessageId) return;

            if (action === 'hide') {
              setMessages((prev) =>
                prev.map((msg) => {
                  // 投稿者本人のメッセージは非表示にしない
                  if (msg.messageId === targetMessageId) {
                    const isOwnMessage = currentUserId && msg.userId === currentUserId;
                    return isOwnMessage
                      ? { ...msg, status: 'hidden' as const } // statusのみ更新
                      : { ...msg, status: 'hidden' as const, isHidden: true }; // 他人のメッセージは非表示
                  }
                  return msg;
                }),
              );
              onModeration?.('hide', targetMessageId);
            } else if (action === 'unhide') {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.messageId === targetMessageId
                    ? { ...msg, status: 'visible' as const, isHidden: false }
                    : msg,
                ),
              );
              onModeration?.('unhide', targetMessageId);
            }
          }

          if (payload.type === 'error') {
            const errorMessage = payload.message || '不明なエラーが発生しました';
            console.error('[Chat] サーバーエラー:', payload);
            setError(errorMessage);
            onError?.(new Error(errorMessage));
          }
        }
      } catch (err) {
        console.error('[Chat] メッセージパースエラー:', err);
      }
    };

    ws.onerror = () => {
      // 置き換え済みの古いソケットのイベントは無視
      if (wsRef.current !== ws) return;
      if (!isMountedRef.current) return;
      setError('ネットワークの接続を確認してください');
      onError?.(new Error('WebSocket error'));
    };

    ws.onclose = (event) => {
      // 置き換え済みの古いソケットのoncloseは無視する。
      // ここでwsRefをnull化・再接続すると、生きている新しいソケットが
      // 参照を失ったまま残り（ゾンビ接続）メッセージが多重受信される
      if (wsRef.current !== ws) return;
      clearKaWatchdog();
      if (!isMountedRef.current) {
        setIsConnected(false);
        wsRef.current = null;
        return;
      }

      console.log(`[Chat] WebSocket切断: code=${event.code}`);
      setIsConnected(false);
      wsRef.current = null;

      // 自動再接続（指数バックオフ + ジッタ、上限30秒。STU-H2対応）
      // 試行回数での打ち切りは行わない（長時間切断後もリロードなしで復帰させるため）
      if (shouldReconnectRef.current) {
        reconnectAttemptsRef.current = Math.min(reconnectAttemptsRef.current + 1, MAX_BACKOFF_EXPONENT);
        const delay = backoffDelayMs(reconnectAttemptsRef.current);
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };
  }, [tenantId, lectureId, onMessage, onModeration, onError]);

  // ------------------------------------------------------------------
  // WebSocket 切断
  // ------------------------------------------------------------------
  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (kaWatchdogTimerRef.current) {
      clearTimeout(kaWatchdogTimerRef.current);
      kaWatchdogTimerRef.current = null;
    }

    if (wsRef.current) {
      const ws = wsRef.current;
      ws.onerror = null;
      ws.onclose = null;
      ws.onopen = null;
      ws.onmessage = null;

      if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      wsRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // ------------------------------------------------------------------
  // メッセージ送信（HTTP POST 経由）
  // AppSync Events では publish は WebSocket 経由でサポートされないため HTTP を使用
  // ------------------------------------------------------------------
  const sendMessage = useCallback(
    async (data: SendMessageData) => {
      const channelPath = `chat/${tenantId}/${lectureId}/messages`;
      await publishMessage(channelPath, {
        ...data,
        userType: data.userType || 'student',
      });
    },
    [tenantId, lectureId],
  );

  // ------------------------------------------------------------------
  // ライフサイクル1: 履歴取得
  // WebSocket 接続（connectEnabled）とは独立して実行する。
  // アーカイブ視聴（接続なし・履歴のみ）でも過去ログを表示できるようにするため。
  // AbortController で Strict Mode の二重マウント時に前回の非同期処理を安全にキャンセル。
  // 初回取得の失敗はバックオフ付きでリトライする（STU-8対応）。
  // 従来はリトライがなく、次の再接続（再購読時の再同期）まで履歴が欠落したままだった。
  // リトライ上限到達後も、WebSocket 再購読時の履歴再同期で回復し得る
  // ------------------------------------------------------------------
  useEffect(() => {
    // currentUserIdが有効な場合のみ取得する（空文字、'anonymous'、空白文字のみの場合は取得しない）
    if (!enabled || !tenantId || !lectureId || !currentUserId || currentUserId === 'anonymous' || currentUserId.trim() === '') {
      // 有効→無効に切り替わった場合、中断された取得の「読み込み中」表示が残らないようにする
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    const MAX_HISTORY_RETRIES = 5;

    const run = async () => {
      const succeeded = await loadHistory(abortController.signal);
      if (succeeded || abortController.signal.aborted) return;
      if (attempt >= MAX_HISTORY_RETRIES) {
        console.warn('[Chat] メッセージ履歴の初回取得リトライ上限に到達（再購読時の再同期に委ねる）');
        return;
      }
      attempt += 1;
      const delay = backoffDelayMs(attempt);
      console.log(`[Chat] メッセージ履歴の取得を ${delay}ms 後に再試行します (試行 ${attempt}/${MAX_HISTORY_RETRIES})`);
      retryTimer = setTimeout(run, delay);
    };
    void run();

    return () => {
      abortController.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tenantId, lectureId, currentUserId]);

  // ------------------------------------------------------------------
  // ライフサイクル2: WebSocket 接続
  // connectEnabled=false の間は接続しない（アーカイブ視聴・lectureData 読込前）
  // ------------------------------------------------------------------
  useEffect(() => {
    isMountedRef.current = true;

    console.log('[useRealtimeChat][DEBUG] useEffect実行:', {
      enabled,
      connectEnabled,
      tenantId,
      lectureId,
      currentUserId,
      shouldConnect:
        enabled && connectEnabled && !!tenantId && !!lectureId && !!currentUserId && currentUserId !== 'anonymous',
    });

    // currentUserIdが有効な場合のみ接続を試みる（空文字、'anonymous'、空白文字のみの場合は接続しない）
    if (!enabled || !connectEnabled || !tenantId || !lectureId || !currentUserId || currentUserId === 'anonymous' || currentUserId.trim() === '') {
      console.log('[useRealtimeChat][DEBUG] 接続条件を満たさない、接続しない');
      return;
    }

    // このマウント（講義）での初回購読からやり直すため、再接続判定フラグをリセット
    hasSubscribedOnceRef.current = false;

    connect();

    return () => {
      isMountedRef.current = false;
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, connectEnabled, tenantId, lectureId, currentUserId]);

  // ------------------------------------------------------------------
  // ネットワーク復帰・タブ復帰時の即時再接続
  // （モバイルはバックグラウンドで OS が WebSocket を切断するため、
  //   バックオフ待ちをスキップして即座に接続を回復させる）
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!enabled || !connectEnabled) return;

    const reconnectNow = () => {
      if (!isMountedRef.current) return;
      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }
      // 待機中のバックオフタイマーを破棄して即時接続
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      console.log('[Chat] ネットワーク/タブ復帰を検知、即時再接続');
      connect();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reconnectNow();
    };

    // ネットワーク切断の即時検知（offlineイベント）
    // Wi-Fiオフ等ではOSがTCP切断を通知せず、oncloseがTCPタイムアウトまで
    // 数分間発火しない（ゾンビ接続）ことがあるため、明示的にソケットを閉じて
    // onclose経由で切断状態への更新と再接続ループへの移行を行わせる
    const onOffline = () => {
      if (!isMountedRef.current) return;
      const ws = wsRef.current;
      if (!ws) return;
      console.log('[Chat] ネットワーク切断を検知（offlineイベント）');
      setIsConnected(false);
      ws.close(4000, 'Network offline');
    };

    window.addEventListener('online', reconnectNow);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('online', reconnectNow);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, connectEnabled, connect]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const reloadHistory = useCallback(async () => {
    await loadHistory(undefined);
  }, [loadHistory]);

  return {
    messages,
    isConnected,
    isLoading,
    error,
    sendMessage,
    connect,
    disconnect,
    clearMessages,
    reloadHistory,
  };
}
